// Настройки игры: выбор языка и, в демонстрационном режиме, запуск редких бонусов.
//
// Смена языка обновляет только текстовые узлы интерфейса, без перезапуска сцены.
// Список языков берётся не с бэкенда: это перечень статических паков, см. i18n/locale.ts.

import { Container, Graphics, Rectangle, Text, type FederatedWheelEvent, type TextStyleOptions } from 'pixi.js';

import { TITLE_FONT, UI_SHEET, type UiFrame } from '../assets/catalog';
import type { AssetManager, TextureSheet } from '../assets/manager';
import type { Cheat } from '../api/spin';
import { LOCALES, localeName, type Locale } from '../i18n/locale';
import { text, textFamily, translation } from '../i18n/text';
import { sfx } from '../sound/sound';

import { RoundButton, type ButtonFaces } from './button';
import { minTouchableScale, type GameLayout, viewportEdge } from './layout';
import { CONTENT_Y, Popup, pageHeading } from './popup';
import { DESIGN_HEIGHT, DESIGN_WIDTH, EDGE_MARGIN } from './scene';

/** Размер и положение круглой иконки рядом со звуком. */
const BUTTON_GAP: number = 20;

/** Раскрытый список прокручивается внутри доски, а не разрастается поверх её рамки. */
const LANGUAGE_WIDTH: number = 560;
const LANGUAGE_HEIGHT: number = 46;
const LANGUAGE_GAP_Y: number = 10;
const SELECT_Y: number = CONTENT_Y + 40;
const LIST_TOP: number = SELECT_Y + LANGUAGE_HEIGHT / 2 + 18;
const LIST_HEIGHT: number = 360;

/** Кнопки запуска бонуса на демонстрационной странице. */
const DEMO_BUTTON_WIDTH: number = 560;
const DEMO_BUTTON_HEIGHT: number = 64;
const DEMO_BUTTON_GAP: number = 28;
const DEMO_HINT_Y: number = CONTENT_Y + 30;
const DEMO_BUTTON_Y: number = DEMO_HINT_Y + 105;

function iconFaces(texture: TextureSheet<UiFrame>): ButtonFaces {
  // В обычном состоянии шестерёнка должна читаться на тёмном поле так же, как звук.
  return { idle: texture.settingsOn, hover: texture.settingsOn, active: texture.settingsOn };
}

function languageStyle(family: string): TextStyleOptions {
  return {
    fontFamily: textFamily(family),
    fontSize: 23,
    fontWeight: 'bold',
    fill: 0xf3dcae,
    align: 'center',
    letterSpacing: 1,
  };
}

/** Медная строка языка; выбранный вариант подсвечен зелёным. */
interface LanguageButton {
  readonly view: Container;
  refresh(): void;
}

function languageButton(locale: Locale, family: string, onSelect: (locale: Locale) => void): LanguageButton {
  const view = new Container();
  let selected: boolean = locale === translation().locale;

  const plate = new Graphics()
    .roundRect(-LANGUAGE_WIDTH / 2, -LANGUAGE_HEIGHT / 2, LANGUAGE_WIDTH, LANGUAGE_HEIGHT, 12)
    .fill(selected ? 0x236f43 : 0x26372e)
    .stroke({ color: selected ? 0xf0c061 : 0x9c7138, width: 3 });

  const label = new Text({ text: localeName(locale), style: languageStyle(family) });
  label.anchor.set(0.5);

  view.eventMode = 'static';
  view.cursor = selected ? 'default' : 'pointer';
  view.on('pointerover', () => {
    if (!selected)
      plate.tint = 0xffffcc;
  });
  view.on('pointerout', () => {
    plate.tint = 0xffffff;
  });
  view.on('pointertap', () => {
    if (selected)
      return;

    sfx('button');
    onSelect(locale);
  });

  view.addChild(plate, label);
  const refresh = (): void => {
    selected = locale === translation().locale;
    plate.clear()
      .roundRect(-LANGUAGE_WIDTH / 2, -LANGUAGE_HEIGHT / 2, LANGUAGE_WIDTH, LANGUAGE_HEIGHT, 12)
      .fill(selected ? 0x236f43 : 0x26372e)
      .stroke({ color: selected ? 0xf0c061 : 0x9c7138, width: 3 });
    plate.tint = 0xffffff;
    view.cursor = selected ? 'default' : 'pointer';
  };

  return { view, refresh };
}

/**
 * Раскрывающийся выбор языка. Колесо над списком прокручивает его, не листая страницу попапа.
 *
 * Список намеренно живёт одним длинным узлом под маской: все языки уже созданы и принимают
 * клик, а прокрутка двигает только этот узел.
 */
interface LanguageDropdown {
  readonly view: Container;
  close(): void;
  refreshText(): void;
}

function languageDropdown(assets: AssetManager, onSelect: (locale: Locale) => void): LanguageDropdown {
  const view = new Container();
  const family: string = assets.font(TITLE_FONT);
  const selected: Locale = translation().locale;

  const selection = new Container();
  const selectedPlate = new Graphics()
    .roundRect(-LANGUAGE_WIDTH / 2, -LANGUAGE_HEIGHT / 2, LANGUAGE_WIDTH, LANGUAGE_HEIGHT, 12)
    .fill(0x236f43)
    .stroke({ color: 0xf0c061, width: 3 });
  const selectedLabel = new Text({ text: localeName(selected), style: languageStyle(family) });
  selectedLabel.anchor.set(0.5);
  const arrow = new Text({ text: '▾', style: { ...languageStyle(family), fontSize: 30 } });
  arrow.anchor.set(0.5);
  arrow.position.set(LANGUAGE_WIDTH / 2 - 28, -2);
  selection.eventMode = 'static';
  selection.cursor = 'pointer';
  selection.addChild(selectedPlate, selectedLabel, arrow);
  selection.position.set(0, SELECT_Y);

  // Маска повторяет скругление панели: крайние строки не вылезают в её углы при прокрутке.
  const clip = new Graphics().roundRect(-LANGUAGE_WIDTH / 2, LIST_TOP, LANGUAGE_WIDTH, LIST_HEIGHT, 12).fill(0xffffff);
  const listPanel = new Graphics()
    .roundRect(-LANGUAGE_WIDTH / 2, LIST_TOP, LANGUAGE_WIDTH, LIST_HEIGHT, 12)
    .fill(0x17251f)
    .stroke({ color: 0x9c7138, width: 3 });
  const list = new Container();
  const viewport = new Container();
  viewport.mask = clip;
  viewport.eventMode = 'static';
  viewport.hitArea = new Rectangle(-LANGUAGE_WIDTH / 2, LIST_TOP, LANGUAGE_WIDTH, LIST_HEIGHT);
  viewport.addChild(list);

  // Обработчики строк создаются раньше, чем сама функция закрытия списка.
  // Присваиваем её ниже, когда уже известен флаг `open`.
  let close: () => void = () => {};
  const buttons: LanguageButton[] = [];

  for (let index = 0; index < LOCALES.length; index++) {
    const button = languageButton(LOCALES[index], family, (locale) => {
      close();
      onSelect(locale);
    });
    button.view.position.set(0, LIST_TOP + LANGUAGE_HEIGHT / 2 + index * (LANGUAGE_HEIGHT + LANGUAGE_GAP_Y));
    buttons.push(button);
    list.addChild(button.view);
  }

  const listHeight: number = LOCALES.length * (LANGUAGE_HEIGHT + LANGUAGE_GAP_Y) - LANGUAGE_GAP_Y;
  const maxScroll: number = Math.max(listHeight - LIST_HEIGHT, 0);
  let scroll = 0;
  const scrollTo = (next: number): void => {
    scroll = Math.min(Math.max(next, 0), maxScroll);
    list.y = -scroll;
  };

  viewport.on('wheel', (event: FederatedWheelEvent) => {
    event.preventDefault();
    scrollTo(scroll + event.deltaY);
  });

  let open = false;
  const showList = (shown: boolean): void => {
    open = shown;
    viewport.visible = open;
    clip.visible = open;
    listPanel.visible = open;
    arrow.text = open ? '▴' : '▾';
  };
  selection.on('pointertap', () => {
    sfx('button');
    showList(!open);
  });

  close = (): void => showList(false);

  const heading = pageHeading(text('settingsOptions'), family, assets);
  const refreshText = (): void => {
    (heading.children[0] as Text).text = text('settingsOptions');
    selectedLabel.text = localeName(translation().locale);
    for (const button of buttons)
      button.refresh();
  };

  close();
  view.addChild(heading, listPanel, selection, clip, viewport);
  return { view, close, refreshText };
}

/** Собирает одну страницу с раскрывающимся и прокручиваемым списком всех локалей. */
export function languagePages(assets: AssetManager, onSelect: (locale: Locale) => void = () => {}): Container[] {
  return [languageDropdown(assets, onSelect).view];
}

/** Медная кнопка, которой тестировщик запускает один из редких бонусов. */
interface DemoButton {
  readonly view: Container;
  setCaption(caption: string): void;
}

function demoButton(caption: string, y: number, onClick: () => void, family: string): DemoButton {
  const view = new Container();
  const plate = new Graphics()
    .roundRect(-DEMO_BUTTON_WIDTH / 2, -DEMO_BUTTON_HEIGHT / 2, DEMO_BUTTON_WIDTH, DEMO_BUTTON_HEIGHT, 14)
    .fill(0x236f43)
    .stroke({ color: 0xf0c061, width: 3 });
  const label = new Text({ text: caption, style: languageStyle(family) });
  label.anchor.set(0.5);

  view.eventMode = 'static';
  view.cursor = 'pointer';
  view.position.set(0, y);
  view.on('pointerover', () => {
    plate.tint = 0xffffcc;
  });
  view.on('pointerout', () => {
    plate.tint = 0xffffff;
  });
  view.on('pointertap', () => {
    sfx('button');
    onClick();
  });
  view.addChild(plate, label);

  return { view, setCaption: (next: string): void => { label.text = next; } };
}

interface DemoPage {
  readonly view: Container;
  refreshText(): void;
}

function demoPage(assets: AssetManager, onCheat: (cheat: Cheat) => void): DemoPage {
  const view = new Container();
  const family: string = assets.font(TITLE_FONT);
  const heading = pageHeading(text('demoMode'), family, assets);
  const hint = new Text({ text: text('demoModeHint'), style: languageStyle(family) });
  const steamSpin = demoButton(text('demoSteamSpin'), DEMO_BUTTON_Y, () => onCheat('steamSpin'), family);
  const redHotSpin = demoButton(text('demoRedHotSpin'), DEMO_BUTTON_Y + DEMO_BUTTON_HEIGHT + DEMO_BUTTON_GAP, () => onCheat('redHotSpin'), family);
  hint.anchor.set(0.5);
  hint.position.set(0, DEMO_HINT_Y);

  const refreshText = (): void => {
    (heading.children[0] as Text).text = text('demoMode');
    hint.text = text('demoModeHint');
    steamSpin.setCaption(text('demoSteamSpin'));
    redHotSpin.setCaption(text('demoRedHotSpin'));
  };

  view.addChild(heading, hint, steamSpin.view, redHotSpin.view);
  return { view, refreshText };
}

/** Страница настроек, доступная только когда сцена передала действия демонстрационного режима. */
export function demoPages(assets: AssetManager, onCheat: (cheat: Cheat) => void): Container[] {
  return [demoPage(assets, onCheat).view];
}

/** Иконка настроек и её модальное окно. */
export class Settings {
  readonly view: Container = new Container();

  readonly #button: RoundButton;
  #popup: Popup;
  readonly #onOpen: () => void;
  readonly #dropdown: LanguageDropdown;
  readonly #demo: DemoPage | null;

  constructor(
    assets: AssetManager,
    onOpen: () => void = () => {},
    onSelect: (locale: Locale) => void = () => {},
    onCheat: ((cheat: Cheat) => void) | null = null,
  ) {
    const ui: TextureSheet<UiFrame> = assets.sheet(UI_SHEET);
    this.#onOpen = onOpen;

    this.#button = new RoundButton(iconFaces(ui), () => this.#open());
    this.#button.view.position.set(
      DESIGN_WIDTH / 2 - EDGE_MARGIN - this.#button.radius * 3 - BUTTON_GAP,
      -DESIGN_HEIGHT / 2 + EDGE_MARGIN + this.#button.radius,
    );

    this.#dropdown = languageDropdown(assets, onSelect);
    const pages: Container[] = [this.#dropdown.view];
    if (onCheat !== null) {
      this.#demo = demoPage(assets, (cheat: Cheat) => {
        this.#popup.close();
        onCheat(cheat);
      });
      pages.push(this.#demo.view);
    } else {
      this.#demo = null;
    }
    this.#popup = new Popup(assets, text('settings'), pages);
    this.view.addChild(this.#button.view);
  }

  /** Узел попапа. Его кладёт в свой слой сцена, см. ADR-54 и ui/help.ts. */
  get popups(): readonly Container[] {
    return [this.#popup.view];
  }

  update(seconds: number): void {
    this.#button.update(seconds);
    this.#popup.update(seconds);
  }

  layout(layout: GameLayout): void {
    const { bounds, composition, viewport } = layout;
    const mobile: boolean = composition === 'mobile';
    const scale: number = mobile ? minTouchableScale(viewport, this.#button.radius, 22) : 1;
    const radius: number = this.#button.radius * scale;
    const edge: number = mobile ? viewportEdge(viewport) : EDGE_MARGIN;
    const gap: number = mobile ? BUTTON_GAP / viewport.scale : BUTTON_GAP;

    this.#button.view.scale.set(scale);
    this.#button.view.position.set(
      bounds.right - edge - radius * 3 - gap,
      bounds.top + edge + radius,
    );
    this.#popup.layout(viewport);
  }

  destroy(): void {
    this.#popup.destroy();
  }

  close(): void {
    this.#popup.close();
  }

  /** Переписывает надписи, сохраняя popup и состояние раскрытого списка. */
  refreshText(): void {
    this.#popup.title = text('settings');
    this.#dropdown.refreshText();
    this.#demo?.refreshText();
  }

  #open(): void {
    this.#onOpen();
    this.#popup.show();
  }
}
